import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black } from '../../../components/Logo/Logo';
import { API_CONFIG } from '../../../config/api';
import './WindmillGame.css';

const getApiUrl = (path) => `${API_CONFIG.baseURL}${path.startsWith('/') ? path : `/${path}`}`;

const GameStudio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studioLoading, setStudioLoading] = useState(false);
  const [gameId, setGameId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showQuestion, setShowQuestion] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [caseStudyInput, setCaseStudyInput] = useState('');
  const [subject, setSubject] = useState('General');
  const [difficulty, setDifficulty] = useState('Intermediate');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const appData = await appsAPI.getAppById(id);
        setApp(appData);
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  const pollManifest = async (newGameId) => {
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i += 1) {
      const res = await fetch(getApiUrl(`/api/game-studio/status/${newGameId}`));
      const status = await res.json();
      if (status?.status === 'completed') {
        const manifestRes = await fetch(getApiUrl(`/api/game-studio/game/${newGameId}`));
        const manifest = await manifestRes.json();
        const list = Array.isArray(manifest?.questions) ? manifest.questions : [];
        setQuestions(list);
        setCurrentVideo(list[0]?.video_url || '');
        return true;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    return false;
  };

  const startGeneration = async () => {
    if (!caseStudyInput.trim()) return;
    try {
      setStudioLoading(true);
      const token = appsAPI.getAccessToken();
      const response = await fetch(getApiUrl('/api/game-studio/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          title: app?.title || 'Game Studio',
          case_study_text: caseStudyInput,
          subject,
          difficulty,
        }),
      });
      const payload = await response.json();
      if (!payload?.game_id) return;
      setGameId(payload.game_id);
      await pollManifest(payload.game_id);
    } finally {
      setStudioLoading(false);
    }
  };

  const onVideoEnded = () => {
    if (currentIndex >= questions.length) return;
    setShowQuestion(true);
  };

  const submitAnswer = async (choice) => {
    const q = questions[currentIndex];
    if (!q) return;
    const response = await fetch(getApiUrl('/api/game-studio/answer'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, question_id: q.id, choice, user_id: user?.id || 'default' }),
    });
    const result = await response.json();
    setFeedback(result);
    setShowQuestion(false);
    setShowFeedback(true);
  };

  const continueFlow = () => {
    setShowFeedback(false);
    const nextIndex = currentIndex + 1;
    if (nextIndex >= questions.length) {
      setShowQuestion(true);
      return;
    }
    setCurrentIndex(nextIndex);
    setCurrentVideo(questions[nextIndex]?.video_url || '');
    const el = videoRef.current;
    if (el) {
      el.src = questions[nextIndex]?.video_url || '';
      el.load();
      el.play().catch(() => {});
    }
  };

  const userName = user?.name || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  if (loading) return <div className="windmill-game-loading"><p>Loading game studio...</p></div>;

  return (
    <div className="windmill-game-page">
      <header className="windmill-game-header">
        <div className="windmill-game-header-content">
          <div className="windmill-game-left-section">
            <button className="back-button" onClick={() => navigate(`/apps-store/${id}`)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" /></svg>
            </button>
            <div className="windmill-game-logo"><Logo_black onClick={() => navigate('/apps-store')} /></div>
            <h1 className="windmill-game-title">{app?.title || 'Game Studio'}</h1>
          </div>
        </div>
      </header>

      <main className="windmill-game-main">
        {!questions.length ? (
          <div className="windmill-question-panel" style={{ maxWidth: '920px', margin: '40px auto' }}>
            <div className="windmill-question-title">GAME STUDIO</div>
            <div className="windmill-question-subtitle">Create questions and linked videos from your case study</div>
            <textarea
              value={caseStudyInput}
              onChange={(e) => setCaseStudyInput(e.target.value)}
              placeholder="Paste your case study text here..."
              rows={10}
              style={{ width: '100%', marginTop: '16px', borderRadius: '12px', padding: '12px', background: 'var(--iv-band)', color: 'var(--iv-on-band)', border: '1px solid var(--iv-band-line)' }}
            />
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" style={{ flex: 1, borderRadius: '10px', padding: '10px' }} />
              <input value={difficulty} onChange={(e) => setDifficulty(e.target.value)} placeholder="Difficulty" style={{ flex: 1, borderRadius: '10px', padding: '10px' }} />
            </div>
            <button className="windmill-continue-btn" onClick={startGeneration} disabled={studioLoading} style={{ marginTop: '16px' }}>
              {studioLoading ? 'Generating...' : 'Generate Game'}
            </button>
          </div>
        ) : (
          <div className="windmill-video-container">
            <video
              ref={videoRef}
              className="windmill-main-video"
              autoPlay
              muted
              playsInline
              onEnded={onVideoEnded}
            >
              <source src={currentVideo} type="video/mp4" />
            </video>

            {showQuestion && questions[currentIndex] && (
              <div className="windmill-overlay active">
                <div className="windmill-question-panel">
                  <div className="windmill-question-title">{questions[currentIndex].title}</div>
                  <div className="windmill-question-subtitle">{questions[currentIndex].subtitle}</div>
                  <div className="windmill-question-text">{questions[currentIndex].question}</div>
                  <div className="windmill-options">
                    {(questions[currentIndex].options || []).map((opt) => (
                      <div key={opt.id} className="windmill-option-button" onClick={() => submitAnswer(opt.id)}>
                        <span className="windmill-option-letter">{opt.id}</span>
                        <div className="windmill-option-title">{opt.text}</div>
                        <div className="windmill-option-desc">{opt.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {showFeedback && feedback && (
              <div className={`windmill-feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
                <div className="windmill-feedback-title">{feedback.correct ? 'CORRECT' : 'TRY AGAIN'}</div>
                <div className="windmill-feedback-text">{feedback.explanation}</div>
                <button className="windmill-continue-btn" onClick={continueFlow}>Continue</button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default GameStudio;
